import React from 'react'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
import { PricingCardCTAProps } from './types'

const PricingCardCTA = ({
  badgeColor,
  badgeText,
  children, // styledList
  ctaLabel,
  ctaLink,
  packagePrice,
  packagePriceMonthly = '',
  pricePrefix,
  priceSuffix,
  priceStrikethrough,
  sectionDescription,
  sectionHeading,
  image,
  bottomLink,
  dataTestId = 'PricingCardCTA-1'
}: PricingCardCTAProps) => {
  const convertSymbolsToWords = (text: string): string => {
    if (!text) return text

    // Replace symbols with words
    let converted = text.replace(/\//g, ' per ').replace(/\+/g, ' plus ')

    // Remove any double spaces
    converted = converted.replace(/\s+/g, ' ').trim()

    return converted
  }

  const renderImage = () => {
    if (image) {
      const query = ContentfulImageAPI.transformImageAvif({
        width: 900,
        extension: image.file?.contentType
      })
      const srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
        url: image.file?.url,
        widths: [900, 736, 393],
        extension: image.file?.contentType
      })
      const imgResolutions = {
        imageResolutions: [
          {
            borderRadius: 'Small',
            desktop: [
              {
                optimizedImages: {
                  src: image.file?.url,
                  srcsetAvif: srcsetWeb ? srcsetWeb : image.file?.url,
                  srcAvif: image.file?.url + query
                },
                url: image.file?.url,
                title: '',
                description: '',
                extension: image.file?.contentType
              }
            ]
          }
        ]
      }
      return (
        <div className='w-full'>
          <ImageResolutions images={imgResolutions} />
        </div>
      )
    }
  }

  const listItems: any[] = []
  if (Array.isArray(children)) {
    children.forEach((child: any) => {
      if (Array.isArray(child.listItems) && child.listItems.length > 0) {
        listItems.push({
          ...child.listItems[0],
          listStyles: child.listStyles
        })
      }
    })
  }

  return (
    <div
      className='bg-white mt-[26px] relative rounded-md border-card-light pricing-card'
      data-testid={dataTestId}
    >
      <div className='ds-grid gap-y-0 p-6'>
        <div className='col-span-4'>
          {badgeText && badgeColor ? (
            <div className='mb-2'>
              <Badge badgeColor={badgeColor} text={badgeText} uppercase />
            </div>
          ) : null}
          <div>
            {sectionHeading ? (
              <Typography
                as='h4'
                classes='mb-[10px] sm:mb-4 !font-normal'
                variation='h4'
              >
                {sectionHeading}
              </Typography>
            ) : null}
            {sectionDescription ? (
              <Typography as='p' classes='text-secondary' variation='default'>
                {sectionDescription}
              </Typography>
            ) : null}
          </div>
          <div className='mt-4 self-center'>
            {pricePrefix ? (
              <Typography as='p' classes='text-secondary' variation='default'>
                {pricePrefix}
              </Typography>
            ) : null}
            {priceStrikethrough ? (
              <Typography
                as='p'
                classes='text-gray-50 line-through !font-normal'
                variation='h4'
              >
                {`<span>
                  <s>
                    <span class='sr-only'>Original price:</span>
                    ${priceStrikethrough}
                  </s>
                </span>`}
              </Typography>
            ) : null}
            {packagePrice ? (
              <p>
                <Typography
                  as='span'
                  classes='!text-[31px] !leading-[40px] !text-default !font-effra'
                  variation='default'
                >
                  {packagePrice}
                </Typography>
                {packagePriceMonthly ? (
                  <Typography
                    ariaLabel={convertSymbolsToWords(packagePriceMonthly)}
                    as='span'
                    classes='text-secondary'
                    variation='default'
                  >
                    {packagePriceMonthly}
                  </Typography>
                ) : null}
              </p>
            ) : null}
            {priceSuffix ? (
              <Typography
                ariaLabel={convertSymbolsToWords(priceSuffix)}
                as='p'
                classes='text-secondary'
                variation='default'
              >
                {priceSuffix}
              </Typography>
            ) : null}
          </div>
          <div className='mt-4 sm:mt-6'>
            {ctaLabel && ctaLink ? (
              <Button
                fontWeight={['font-semibold']}
                href={ctaLink}
                size='medium'
                variation='primary'
              >
                {ctaLabel}
              </Button>
            ) : null}
          </div>
        </div>
        <div className='col-span-4 mt-4 sm:mt-0'>
          {listItems.length > 0 && (
            <StyledList
              listItems={listItems}
              listOnly
              listStyles='none'
              listVariation='heading-only'
              sectionDescription=''
              sectionHeading=''
            />
          )}
          {bottomLink ? (
            <div className='mt-5 pt-2 border-t border-t-gray-30'>
              <TextLink
                classes='mt-2'
                description={`${sectionHeading} ${bottomLink.label}`}
                href={bottomLink.url}
                icon={Icon.linkIcons(bottomLink.icon)}
                text={bottomLink.label}
              />
            </div>
          ) : null}
        </div>
        <div className='col-span-4 content-center mt-[31px] sm:mt-0 mb-5 sm:mb-0'>
          {image ? renderImage() : null}
        </div>
      </div>
    </div>
  )
}

export default PricingCardCTA
